
import { defineComponent, computed, ref, onMounted } from "vue";
import GameChat from "./GameChat.vue";
import GameStatus from "./GameStatus.vue";
import SettingsModal from "./SettingsModal.vue";
import SticksModal from "./SticksModal.vue";
import TournamentModal from "./TournamentModal.vue";
import UserModal from "./UserModal.vue";
import HelpModal from "./HelpModal.vue";
import InfoModal from "./InfoModal.vue";
import DonateModal from "./DonateModal.vue";
import InvitationConfirmModal from "./InvitationConfirmModal.vue"; // send game invite
import InvitationRequestModal from "./InvitationRequestModal.vue"; // accept or reject game invite
import BigHitPositionModal from "./BigHitPositionModal.vue";
import WaitForOpponentModal from "./WaitForOpponentModal.vue";
import JoinTournamentModal from "./JoinTournamentModal.vue";
import ConcedeGameModal from "./ConcedeGameModal.vue";
import gameApplication from "../../game/game-app";
import { useStore } from "@/stores";
import { AbstractPlayer } from "../../types/abstract-player";
import { MutationType } from "@/stores/mutation-types";
import { HitCueBallMessage } from "@/protocol/messages/hit-cue-ball-message";
import { Vector2 } from "@/math/vector2";
import { HitPointMessage } from "@/protocol/messages/hit-point-message";
import { Constants } from "@/math/constants";

export default defineComponent({
  emits: [
    'selectOpponent', // when clicking on invitee in the chat panel
    'confirmInvitePlayer', // when confirming the game invite in the modal
  ],

  data: () => ({
    tableHeight: 0,
    maxPower: 50,
    whiteHitPositionBallRadus: 25, // small white ball diameter is 50px
    isSafari: false,

    position: {
      top: 0,
      left: 0,
      isDraging: false,
      initTop: 0,
      initLeft: 0,
    },

    /**
     * Main game modals.
     */
    isSticksShown: false,
    isSettingsShown: false,
    isHelpShown: false,
    isDonateShown: false,
    isUserShown: false,
    isTournamentShown: false,
    isBigHitPositionShown: false,

    /**
     * These are used when initiating a new game creation after clicking
     * on the name of the invitee in the chat panel.
     */
    inviteePlayerId: '',
    inviteePlayerName: '',
    isInvitationConfirmModalShown: false,
    isConcedeGameModalShown: false,
    
    gameTable: '/images/table-green.png'
  }),

  setup() {
    const store = useStore();
    const windowWidth = ref(window.innerWidth);
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

    const invitedPlayerId = computed(() => store.state.invitedPlayerId);
    const gameMode = computed(() => store.state.selectedGameKind);
    const isBigHitPosition = computed(() => store.state.settings.hitPositionSize === "LARGE_BALL");
    
    onMounted(() => {
      window.addEventListener('resize', () => {
        windowWidth.value = window.innerWidth;
        // Re-draw the menu (splash screen) if it's active.
        if (gameApplication.menu.active === true) {
          gameApplication.menu.draw();
        }
      });
    });

    // modal events (Vuex)
    const isInvitationReqestModalShown = computed(() => store.state.isShowInvitationRequestModal);
    const isWaitForOpponentModalShown = computed(() => store.state.isShowWaitForOpponentModal);
    const isInfoModalShown = computed(() => store.state.isShowInfoModal);
    const isJoinTournamentModalShown = computed(() => store.state.isJoinTournamentModal);

    /**
     * If the user has selected the practice game start the game immediately.
     * FIXME: The timeout is needed because a delay is required to wait for 
     * the response from the server after the game kind is selected.
     */
    setTimeout(() => {
      console.log("Game kind ", gameApplication.getGameKind());
      if (gameApplication.getGameKind() === 'PRACTICE') {
        gameApplication.startPractice();
      }
    }, 1000);
    
    return {
      store,
      invitedPlayerId,
      gameMode,
      isBigHitPosition,
      isInfoModalShown,
      isInvitationReqestModalShown,
      isWaitForOpponentModalShown,
      isJoinTournamentModalShown,
      isSafari, 
    };
  },

  beforeMount() {
    document.title = 'Booza Games - Play Pool and Snooker With Friends';
  },

  mounted() {
    window.addEventListener('resize', this.setTableHeight);
    
    // This is where the main game animation is drawn.
    // If a game is not being played the menu (splash screen) is drawn.
    // If a game is being played the game animation for the game type is started.
    gameApplication.init();

    // custom events
    gameApplication.onEvent('START_GAME', this.startGame);
    gameApplication.onEvent('HIT_CUE_BALL', this.updatePowerIndicator);
    gameApplication.onEvent('OPPONENT_HIT_CUE_BALL', this.opponentHitCueBall);
    gameApplication.onEvent('OPPONENT_ADJUST_HIT_POSITION', this.opponentSetHitPosition);
    gameApplication.onEvent('OK', this.resetHit);
    gameApplication.onEvent('PLAYER_CHANGE', this.resetHit);
    gameApplication.onEvent('END_TURN', this.resetHit);
    gameApplication.onEvent('END_GAME', this.resetHit);
    
    // custom modal events
    gameApplication.onEvent('CONCEDE_GAME', () => { this.isConcedeGameModalShown = true; });
    gameApplication.onEvent('OPEN_DONATE', () => { this.isDonateShown = true; gameApplication.getBillardGame().stick.active = false; });
    gameApplication.onEvent('OPEN_HELP', () => { this.isHelpShown = true; gameApplication.getBillardGame().stick.active = false; });
    gameApplication.onEvent('OPEN_SETTINGS', () => { this.isSettingsShown = true; gameApplication.getBillardGame().stick.active = false; });
  },

  unmounted() {
    window.removeEventListener('resize', this.setTableHeight);
  },

  methods: {
    startGame(): void {   
      this.resetHit();
    },

    setTableHeight(): void {
      const MARGIN = 30;
      const el: any = this.$refs.table;
      const clientHeight = el.clientHeight < 450 ? 421 : el.clientHeight;
      this.tableHeight = clientHeight - MARGIN;
    },

    opponentHitCueBall(message: HitCueBallMessage): void {
      const hitPoint = new Vector2(message.hitPointX, message.hitPointY);
      this.updatePowerIndicator(message.power);
      gameApplication.getBillardGame().shootCueBall(message.power, message.directionX, hitPoint);
    },

    /**
     * This is invoked when a player clicks on the player name in the
     * chat panel change the guest player username.
     * TODO: This is not yet complete.
     */
    onUpdateDisplayName(abstractPlayer: AbstractPlayer): void {
      gameApplication.updateDisplayName(abstractPlayer);
    },

    /**
     * This is invoked when a player clicks on the player name in the
     * chat panel to initiate a game.
     */
    onInitiateGameInvite(abstractPlayer: AbstractPlayer): void {
      this.inviteePlayerId = abstractPlayer.playerId;
      this.inviteePlayerName = abstractPlayer.displayName;
      this.isInvitationConfirmModalShown = true;
    },

    /**
     * This is invoked when a player accept or reject modal is closed.
     */
    onCloseInvitationRequestModal(): void {
      this.store.commit(MutationType.INVITATION_REQUEST_MODAL, false);
    },

    /**
     * This is invoked when a the wait for opponent modal is closed.
     */
    onCloseWaitForOpponentModal(): void {
      this.store.commit(MutationType.WAIT_FOR_OPPONENT_MODAL, false);
    },

    /**
     * This is invoked when the information modal is closed.
     */
    onCloseInfoModal(): void {
      this.store.commit(MutationType.INFO_MODAL, false);
    },

    /**
     * This is invoked when the join tournament modal is closed.
     */
    onCloseJoinTournamentModal(): void {
      this.store.commit(MutationType.JOIN_TOURNAMENT_MODAL, false);
    },

    /**
     * This is invoked when the close concede game modal is closed.
     */
    onCloseConcedeGameModal(): void {
      this.isConcedeGameModalShown = false;
    },    

    /**
     * Hit power related code functions.
     * Update the hit power on the screen.
     */
    updatePowerIndicator(power: number): void {
      const cuePowerId = document.getElementById('cue-power-id');
      const cuePowerDotId = document.getElementById('cue-power-dot-id');
      if (cuePowerId != null && cuePowerDotId != null) {
        const cuePowerIdHeight = cuePowerId.offsetHeight;
        if (power == 0) {
          cuePowerDotId.style.height = '0px';
        } else if (power <= this.maxPower) {
          const calHeight = (cuePowerIdHeight / this.maxPower) * power;
          cuePowerDotId.style.height = calHeight + 'px';
        } else if (power > this.maxPower) {
          cuePowerDotId.style.height = cuePowerIdHeight + 'px';
        } 
      }
    },

    //
    // Hit position related code functions.
    // 
    mouseEnter(event: MouseEvent): void {
      this.position.isDraging = false;
      gameApplication.getBillardGame().stick.enabled = false; // disable cue stick
    },

    mouseLeave(event: MouseEvent): void {
      this.position.isDraging = false;
      gameApplication.getBillardGame().stick.enabled = true; // enable cue stick       
    },

    mouseDown(event: MouseEvent): void {      
      this.position.isDraging = true;
    },

    mouseUp(event: MouseEvent): void {
      this.position.isDraging = false;
    },

    /**
     * This is only used and necessary in large hit position mode.
     * It is used to set the small hit position after the large hit position is set.
     */
    setBallHitPosition(): void {
      const markerIconEl = document.getElementById('strike-marker-icon');
      const markerX = gameApplication.getBillardGame().hitPosition[0];
      const markerY = gameApplication.getBillardGame().hitPosition[1];
     
      // Move the red marker icon on the screen.
      if (markerX > 0 && markerY > 0) {
        markerIconEl.style.left = ((markerX / Constants.HIT_POSITION_RATIO) - 4) + "px"; 
        markerIconEl.style.top = ((markerY / Constants.HIT_POSITION_RATIO) - 12) + "px";
      }
    },

    /**
     * This is executed when the hit position is dragged.
     */
    mouseMove(event: MouseEvent): void {
      if (!gameApplication.getBillardGame().isOpponentTurn() ||
        gameApplication.getGameKind() === 'PRACTICE') {
        const posX = event.clientX;
        const posY = event.clientY;
        if (this.position.isDraging)  {
          this.setMarkerIcon(posX, posY); // set the hit position on screen 
        }
      }
    },

    /**
     * This is executed when the hit position is clicked.
     */
    ballMouseDown(event: MouseEvent): void {
      if (!gameApplication.getBillardGame().isOpponentTurn() ||
        gameApplication.getGameKind() === 'PRACTICE') {
        if (!this.isBigHitPosition) {
          const { clientX, clientY } = event;
          this.setMarkerIcon(clientX, clientY); // set the hit position on screen 
        } else {
          gameApplication.getBillardGame().stick.active = false;
          this.isBigHitPositionShown = true;
        }
      }
    },

    /**
     * Set the player or opponent hit position (clicked or dragged). 
     */ 
    setMarkerIcon(clientX: number, clientY: number): void {   
      const whiteBallEl = document.getElementById('white-ball-id');
      const markerIconEl = document.getElementById('strike-marker-icon');
      const whiteBallRect = whiteBallEl.getBoundingClientRect();
      const wbWidth = whiteBallEl.clientWidth;
      const wbHeight = whiteBallEl.clientHeight;

      // Calcualte the position to set the red market icon.
      const markerX = clientX - whiteBallRect.left;
      const markerY = clientY - whiteBallRect.top;
      
      if ((markerX > 0 && markerX < wbWidth) && (markerY > 0 && markerY < wbHeight)) {  
        // Move the red marker icon on the screen.
        // These hardcoded values are visual adjustments to the red marker cross.
        markerIconEl.style.left = (markerX - 4) + 'px'; 
        markerIconEl.style.top = (markerY - 12) + 'px';
      } 

      // Move the cue stick hit position (diagional) on the screen.
      gameApplication.getBillardGame().updateHitOrigin(this.calculateHitOrigin(markerX));

      // The screen marker ranges from 1 to the diameter of the white hit position ball.
      // Convert the screen markers to range from -1 to +1 for the hit position vector.
      const positionX = this.calculateHitPosition(markerX);
      const positionY = -this.calculateHitPosition(markerY);

      // Update the hit position vector value for the game.
      gameApplication.getBillardGame().updateHitPosition(new Vector2(positionX, positionY));

      // send the screen hit point to the opponent
      if (gameApplication.getGameKind() !== 'PRACTICE') {
        gameApplication.sendHitPoint(markerX, markerY);  
      } 
    },

    /**
     * This is executed when the opponent adjusts the hit position.
     */
    opponentSetHitPosition(message: HitPointMessage): void {
     const markerIconEl = document.getElementById('strike-marker-icon');
     
      // Move the red marker icon on the screen.
      markerIconEl.style.left = (message.pointX - 4) + 'px'; 
      markerIconEl.style.top = (message.pointY - 12) + 'px';

      // Move the cue stick hit origin on the screen.
      gameApplication.getBillardGame().updateHitOrigin(this.calculateHitOrigin(message.pointX));
    },

    /**
     * Reset the hit position, hit origin and hit strength.
     * This is called when the turn is ended.
     */ 
    resetHit(): void {
      const markerIconEl = document.getElementById('strike-marker-icon');
      const adjustment = 5; // FIXME: this is a temporary adjustment
     
      // Move the red marker icon on the screen.
      markerIconEl.style.left = (this.whiteHitPositionBallRadus - adjustment - 4) + "px"; 
      markerIconEl.style.top = (this.whiteHitPositionBallRadus - adjustment - 12) + "px";

      gameApplication.getBillardGame().hitPosition = [0, 0];

      // Move the cue stick hit position on the screen.
      gameApplication.getBillardGame().updateHitOrigin(this.calculateHitOrigin(this.whiteHitPositionBallRadus - adjustment));
      
      this.updatePowerIndicator(0); // reset hit strength
      gameApplication.getBillardGame().updateHitOrigin(this.calculateHitOrigin(this.whiteHitPositionBallRadus)); // reset hit orgin
      gameApplication.getBillardGame().updateHitPosition(new Vector2(0, 0)); // reset hit position
    },

    /**
     * The "hit origin" used is to update the cue stick position on the screen only. 
     * When the hit position is changed left or right the cue stick position is also
     * adjusted on the screen. This value does not affect the gameplay.
     */ 
    calculateHitOrigin(x: number): number { 
      const whiteHitPositionBallDiameter = this.whiteHitPositionBallRadus * 2;
      x = x > whiteHitPositionBallDiameter ? whiteHitPositionBallDiameter : x;
      x = x < 1 ? 1 : x;
      // These hardcoded values are visual.
      return 16 - (0.8 * x); 
    }, 

    /**
     * Convert the screen markers to range from -1 to +1 for the hit position vector.
     */
    calculateHitPosition(n: number): number {
      const whiteHitPositionBallDiameter = this.whiteHitPositionBallRadus * 2;
      n = n > whiteHitPositionBallDiameter ? whiteHitPositionBallDiameter : n;
      n = n < 1 ? 1 : n;
      return n = (n - this.whiteHitPositionBallRadus) / this.whiteHitPositionBallRadus;
    },
  },

  components: {
    GameChat,
    GameStatus,
    SettingsModal,
    SticksModal,
    TournamentModal,
    UserModal,
    HelpModal,
    DonateModal,
    BigHitPositionModal,
    InvitationConfirmModal,
    InvitationRequestModal,
    InfoModal,
    WaitForOpponentModal,
    JoinTournamentModal,
    ConcedeGameModal,
  },
});
